import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Button, Dropdown } from "react-bootstrap";
import NavigationTabs from "../../distributor-settings/network-access/navigation-tabs";
import "./mspSubscriptionAccounts.scss";
import MspAccountOverview from "./MspAccountOverview";
import MspAccountAbout from "./MspAccountAbout";
import TrialPeriodModal from "../modals/TrialPeriodModal";
import ConvertSubscriptionModal from "../modals/ConvertSubscriptionModal";
import SubscriptionSuspendModal from "../modals/SubscriptionSuspendModal";
import { getPageDataApi } from "../../../Utils/commonApi";
import { useAuth } from "../../context/Auth";
import { useNavigate, useParams, useLocation, Routes, Route, Navigate } from "react-router-dom";
import MspSupportAccess from "./MspSupportAccess";
import useCustomerTempAccess from "../../../hooks/get-customer-temp-access.hook";
import Loader from "../../Common/loader";
import useUserActions from "../../../hooks/actions/user.hooks";
import SuspendOrArchieveEnum from "../../../constants/clients.constants";
import { CustomDeleteModal } from "../../DeleteModal/DeleteModal";
import StackReportSection from "./stack-report-section";
import StackPage from "../../stack/stack.page";
import { APP_ROUTES, ROUTE_PATHS } from "../../../constants/app.constants";
import MspSettings from "../../msp-settings";
import MembersAccessPage from "../../distributor-settings/members-access";
import IntegrationsPage from "../../integrations";
import SubscriptionDetails from "../../subscription-details";
import MyTooltip from "../../Common/my-tooltip/my-tooltip.component";
import MspDetailsTooltip from "./msp-details-tooltip";
import MspOverview from "../../msp-overview";
import useQueryParams from "../../../hooks/query-params.hook";
import MspAccountSubscriptions from "./MspAccountSubscriptions";

const TabKeys = {
  overview: "overview",
  subscription: "subscription",
  supportAccess: "supportAccess",
  about: "about",
  members: "members",
  securityStack: "securityStack",
  stackReport: "stackReport",
  settings: "settings",
  integrations: "integrations",
}

const MspSubscriptionAccountDetails = ({ data, isLoading }) => {
  const { getParam } = useQueryParams();
  const variant = getParam('variant') || 'v1';

  const [pageLoading, setPageLoading] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState()
  const [showDeleteModal, setShowDeleteModal] = useState();
  const {accountId}= useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [showTrialPeriodModal, setShowTrialPeriodModal] = useState(false)
  const [showConvertSubscriptionModal, setShowConvertSubscriptionModal] = useState(false)
  const [showSubscriptionSuspendModal, setShowSubscriptionSuspendModal] = useState(null)
  const {authTokens} = useAuth()
  const { 
    sessionList, 
    activeSession, 
    isLoading: isTempAccessLoading, 
    getTempAccessList 
  } = useCustomerTempAccess();
  const { suspendAccount } = useUserActions();
  const subscriptionId = subscriptionData?.ActiveSubscriptionDetails?.Subscriptions?.[0]?.Id;
  const subscriptionStatus = subscriptionData?.AccountStatus;

  const dropdownOptions = [
    {
      title: "Extend trial",
      description: "Extend free trial period",
      icon: "",
      onClick: () => setShowTrialPeriodModal(true),
      className: "f-grey"
    },
    ...(subscriptionStatus === "Suspended" ? [
      {
        title: "Unsuspend Account",
        description: "Restore account access",
        icon: "/images/base-suspend.svg",
        onClick: () => setShowDeleteModal({
          show: true,
          type: SuspendOrArchieveEnum.SUSPEND,
          deleteHeaderText: "Unsuspend Account",
          deleteBodyText: "Are you sure you want to unsuspend this account?",
          deleteButtonText: "Unsuspend",
          deleteButtonClass: "f-white",
          deleteFunction: () => handleSuspendAccount("", SuspendOrArchieveEnum.SUSPEND, false),
          deleteParams: {
            clientId: accountId,
            statusValue: true,
            reason: "",
          },
        }),
        className: "f-grey"
      }
    ] : [
      {
        title: "Suspend Account",
        description: "Temporary restrict to login",
        icon: "/images/base-suspend.svg",
        onClick: () => setShowSubscriptionSuspendModal({
          show: true,
          type: SuspendOrArchieveEnum.SUSPEND
        }),
        className: "f-grey"
      }
    ]),
    ...(subscriptionStatus === "Deactivated" ? [
      {
        title: "Activate Account",
        description: "Restore account access",
        icon: "/images/base-cross-circle.svg",
        onClick: () => setShowDeleteModal({
          show: true,
          type: SuspendOrArchieveEnum.ARCHIVE,
          deleteHeaderText: "Activate Account",
          deleteBodyText: "Are you sure you want to activate this account?",
          deleteButtonText: "Activate",
          deleteButtonClass: "f-white",
          deleteFunction: () => handleSuspendAccount("", SuspendOrArchieveEnum.ARCHIVE, false),
        }),
        className: "f-grey"
      }
    ] : [
      {
        title: "Deactivate Account",
        description: "Deactivate Account",
        icon: "/images/base-cross-circle.svg",
        onClick: () => setShowSubscriptionSuspendModal({
          show: true,
          type: SuspendOrArchieveEnum.ARCHIVE
        }),
        className: "f-red"
      }
    ])
  ].filter(Boolean);

  const getSubscriptionData = useCallback((accountId) => {
    setPageLoading(true);
    return new Promise((resolve, reject) => {
      getPageDataApi("GetSubscriptionTabAnalytics", authTokens, {
        objId_Customer: accountId,
      }).then((res) => {
        setSubscriptionData(res?.detail)
      }).catch(reject)
      .finally(() => {
        setPageLoading(false);
      });
    });
  }, [authTokens]);

  useEffect(() => {
    if (!accountId) return;
    getTempAccessList(accountId);
    getSubscriptionData(accountId);
  }, [accountId])

  const refreshTempAccessList = useCallback(() => {
    getTempAccessList(accountId)
  }, [accountId, getTempAccessList])

  const activeTab = useMemo(() => {
    const currentPath = location.pathname;
    const routes = ROUTE_PATHS.subscriptionPortalPages.mspAccountDetailsPages;
    
    for (const [tabKey, routeFn] of Object.entries(routes)) {
      if (typeof routeFn !== "function") continue;
      if (tabKey === 'settingsPages') continue; // Skip nested settings pages

      const routePath = routeFn(accountId);
      if (currentPath === routePath || currentPath.startsWith(routePath + '/')) {
        return tabKey;
      }
    }
    
    // Check if we're on a settings sub-page
    if (currentPath.includes('/settings/')) {
      return TabKeys.settings;
    }
    
    return TabKeys.overview;
  }, [location.pathname, accountId]);


  const getRoutePath = useCallback((routeKey) => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.set('variant', variant);
    const routeFn = ROUTE_PATHS.subscriptionPortalPages.mspAccountDetailsPages[routeKey];
    const fullPath = typeof routeFn === 'function' ? routeFn(accountId) : routeFn;
    return `${fullPath}?${queryParams.toString()}`;
  }, [accountId, variant, location.search]);

  const navigationTabs = useMemo(() => 
    variant === 'v1' ? [
      { 
        eventKey: TabKeys.overview, 
        label: "Overview", 
        active: activeTab === TabKeys.overview, 
        onClick: () => navigate(getRoutePath(TabKeys.overview))
      },
      { 
        eventKey: TabKeys.subscription, 
        label: "Subscription", 
        active: activeTab === TabKeys.subscription, 
        onClick: () => navigate(getRoutePath(TabKeys.subscription)),
      },
      {
        eventKey: TabKeys.about, 
        label: "About", 
        active: activeTab === TabKeys.about, 
        onClick: () => navigate(getRoutePath(TabKeys.about)),
      },
      { 
        eventKey: TabKeys.supportAccess, 
        label: "Support Access", 
        active: activeTab === TabKeys.supportAccess, 
        onClick: () => navigate(getRoutePath(TabKeys.supportAccess))
      },
    ] : [
    { 
      eventKey: TabKeys.overview, 
      label: "Overview", 
      active: activeTab === TabKeys.overview, 
      onClick: () => navigate(getRoutePath(TabKeys.overview))
    },
    { 
      eventKey: TabKeys.subscription, 
      label: "Subscription", 
      active: activeTab === TabKeys.subscription, 
      onClick: () => navigate(getRoutePath(TabKeys.subscription)),
    },
    { 
      eventKey: TabKeys.integrations, 
      label: "Integrations", 
      active: activeTab === TabKeys.integrations, 
      onClick: () => navigate(getRoutePath(TabKeys.integrations)),
    },
    {
      eventKey: TabKeys.members, 
      label: "Members", 
      active: activeTab === TabKeys.members, 
      onClick: () => navigate(getRoutePath(TabKeys.members)),
    },
    { 
      eventKey: TabKeys.settings, 
      label: "MSP Settings", 
      active: activeTab === TabKeys.settings, 
      onClick: () => {
        const queryParams = new URLSearchParams(location.search);
        queryParams.set('variant', variant);
        const settingsPath = ROUTE_PATHS.subscriptionPortalPages.mspAccountDetailsPages.settingsPages.companySettings(accountId);
        navigate(`${settingsPath}?${queryParams.toString()}`);
      },
    },
    { 
      eventKey: TabKeys.securityStack, 
      label: "Security Stack", 
      active: activeTab === TabKeys.securityStack, 
      onClick: () => navigate(getRoutePath(TabKeys.securityStack))
    },
    { 
      eventKey: TabKeys.stackReport, 
      label: "Stack Report", 
      active: activeTab === TabKeys.stackReport, 
      onClick: () => navigate(getRoutePath(TabKeys.stackReport))
    },
    { 
      eventKey: TabKeys.supportAccess, 
      label: "Support Access", 
      active: activeTab === TabKeys.supportAccess, 
      onClick: () => navigate(getRoutePath(TabKeys.supportAccess))
    },
  ], [activeTab, navigate, variant, getRoutePath]);

  const handleSuspendAccount = (reason, type, statusValue = true) => {
    return suspendAccount({
      suspendOrArchive: type,
      clientId: accountId,
      statusValue,
      reason: reason,
    }).then(()=>{
      getSubscriptionData(accountId);
    })
  }

  if (isLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center page-loading">
        <Loader />
      </div>
    )
  }

  return (
    <div className="msp-subscription-details">
      <div className="d-flex align-items-center justify-content-between border-bottom pb-2 bg-white pt-3 overflow-x-auto hide-scrollbar no-wrap"
      style={{
        position:"sticky",
        top:"46px",
        zIndex: 5,
        padding: "0 24px"
      }}
      
      >
        <div className="d-flex align-items-center" style={{ fontSize: '16px', lineHeight: '28px', fontWeight: 500 }}>
          <div className="d-flex align-items-center pointer"
            onClick={()=>{
            navigate(ROUTE_PATHS.subscriptionPortalPages.mspAccounts)
            }}
          >
            <img
              src="/images/attack-surface/back-icon.svg"
              alt=""
              className="mr-2"
            />
            <span className="f-grey" style={{ fontSize: '14px', lineHeight: '22px' }}>Back</span>
          </div>
          <span className="f-600 f-grey pl-4">MSP's Accounts</span>
          <span className="f-600 f-grey px-2">&nbsp; &nbsp;/&nbsp;&nbsp;</span>
          <span className="f-600 f-black">
            {data?.UserDetail?.OrgName}
          </span>
        </div>
      </div>
      {/*User Details */}
      <div className="d-flex flex-wrap gap-8 align-items-center justify-content-between header-container">
        <div>
          <div className=" d-flex align-items-center  justify-content-start pl-2  py-2 ">
            <img
              alt=""
              src={data?.UserDetail?.Avatar}
              className="ml-1 mr-3 rounded-full"
              width={64}
              height={64}
              onError={({ currentTarget }) => {
                currentTarget.onerror = null; // prevents looping
                currentTarget.src = "/images/user-logo.svg";
              }}
            />
            <div className="">
              <div className="d-flex align-items-center">
                <div className="f-600 f-18 f-black m-0">
                  {data?.UserDetail?.OrgName}
                </div>
                {subscriptionStatus && (
                  <MyTooltip
                    renderTooltip={() => (
                      <MspDetailsTooltip />
                    )}
                    isFixed={true}
                    direction="bottom"
                    disabled={variant !== 'v2'}
                  >
                    <div className="customer-status ml-2">
                      {subscriptionStatus}
                    </div>
                  </MyTooltip>
                )}
              </div>
              <div className="f-12 f-darkgrey">
                {data?.CompanyType}
              </div>
            </div>
          </div>
        </div>

        {variant === 'v1' && (
          <div className="d-flex align-items-center">
            <Dropdown className={`transparennt-wrapper-dropdown `}>
              <Dropdown.Toggle>
                <Button className="hg-cancel-btn  mr-2">
                  <img
                    src="/images/settings-v2/settingwheel.svg"
                    alt=""
                    className="mr-2"
                  />
                  Manage account
                </Button>
              </Dropdown.Toggle>
              <Dropdown.Menu className="p-2 pointer d-flex flex-column gap-4"
                style={{
                  width:"200px"
                }}
              >
                {dropdownOptions.map((option, index) => (
                  <div 
                    key={index}
                    className="d-flex bg-hover-grey"
                    onClick={option.onClick}
                  >
                    <div>
                      <div className="d-flex align-items-center gap-4">
                        {option.icon && <img src={option.icon} alt="" />}
                        <div className={`f-14 ${option.className}`}>{option.title}</div>
                      </div>
                      <div className="f-12 f-darkgrey">
                        {option.description}
                      </div>
                    </div>
                  </div>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        )}
      </div>
      <div className="tabs-containera">
        <div className="h-100 d-flex flex-column flex-1">
          <div
            sm={12}
            className=" d-flex justify-content-between align-items-center ml- p-0 overflow-x-auto hide-scrollbar"
          >
            <div className="navs-container w-100">
              <NavigationTabs tabs={navigationTabs} />
            </div>
          </div>
          <div sm={12} className="ml-1 p-0 flex-1 d-flex flex-column">
            <Routes>
              <Route
                path={APP_ROUTES.subscriptionPortalPages.mspAccountDetailsPages.overview}
                element={variant === 'v1' ? <MspAccountOverview accountId={accountId} /> : <MspOverview />}
              />
              <Route
                path={APP_ROUTES.subscriptionPortalPages.mspAccountDetailsPages.subscription}
                element={variant === 'v1' ? <MspAccountSubscriptions pageLoading={pageLoading} subscriptionData={subscriptionData} /> : <SubscriptionDetails />}
              />
              <Route
                path={APP_ROUTES.subscriptionPortalPages.mspAccountDetailsPages.supportAccess}
                element={
                  <MspSupportAccess 
                    accountId={accountId} 
                    sessionList={sessionList}
                    activeSession={activeSession}
                    refreshTempAccessList={refreshTempAccessList}
                    isTempAccessLoading={isTempAccessLoading} 
                  />
                }
              />
              <Route
                path={APP_ROUTES.subscriptionPortalPages.mspAccountDetailsPages.about}
                element={<MspAccountAbout accountId={accountId}/>}
              />
              <Route
                path={APP_ROUTES.subscriptionPortalPages.mspAccountDetailsPages.members}
                element={<MembersAccessPage />}
              />
              <Route
                path={APP_ROUTES.subscriptionPortalPages.mspAccountDetailsPages.securityStack}
                element={<StackPage />}
              />
              <Route
                path={APP_ROUTES.subscriptionPortalPages.mspAccountDetailsPages.stackReport}
                element={<StackReportSection />}
              />
              <Route
                path={`${APP_ROUTES.subscriptionPortalPages.mspAccountDetailsPages.settings}/*`}
                element={<MspSettings accountId={accountId} />}
              />
              <Route
                path={APP_ROUTES.subscriptionPortalPages.mspAccountDetailsPages.integrations}
                element={<IntegrationsPage />}
              />
              <Route path="" element={<Navigate to={accountId ? ROUTE_PATHS.subscriptionPortalPages.mspAccountDetailsPages.overview(accountId) : ROUTE_PATHS.subscriptionPortalPages.mspAccounts} replace />} />
              <Route path="*" element={<Navigate to={accountId ? ROUTE_PATHS.subscriptionPortalPages.mspAccountDetailsPages.overview(accountId) : ROUTE_PATHS.subscriptionPortalPages.mspAccounts} replace />} />
            </Routes>
          </div>
        </div>
      </div>




      <TrialPeriodModal
        show={showTrialPeriodModal}
        hideModal={()=>{
          setShowTrialPeriodModal(false)
        }}
        accountId={accountId}
        subsciptionId={subscriptionId}
        subscriptionData={subscriptionData}
        onSuccess={()=>{
          setShowTrialPeriodModal(false)
          getSubscriptionData(accountId);
        }}
      />
      <ConvertSubscriptionModal
      show={showConvertSubscriptionModal}
      hideModal={()=>{
        setShowConvertSubscriptionModal(false)
      }}
      />
      <SubscriptionSuspendModal
      show={showSubscriptionSuspendModal?.show}
      hideModal={()=>{
        setShowSubscriptionSuspendModal(false)
      }}
      onSuspend={async (reason) => {
        const response = await handleSuspendAccount(reason, showSubscriptionSuspendModal?.type);
        setShowSubscriptionSuspendModal(false)
        return response;
      }}
      type={showSubscriptionSuspendModal?.type}
      />
      <CustomDeleteModal
        type={showDeleteModal?.type}    
        show={showDeleteModal?.show}
        isLoading={showDeleteModal?.loading}
        deleteHeaderText={showDeleteModal?.deleteHeaderText}
        deleteBodyText={showDeleteModal?.deleteBodyText}
        deleteFooterText={showDeleteModal?.deleteFooterText}
        deleteButtonText={showDeleteModal?.deleteButtonText}
        deleteButtonClass={showDeleteModal?.deleteButtonClass}
        hideModal={()=>{
          setShowDeleteModal(false)
        }}
        deleteFunction={async ()=>{
          setShowDeleteModal(prev => ({...prev, loading: true}))
          const response = await showDeleteModal?.deleteFunction();
          setShowDeleteModal(null);
          return response;
        }}
        deleteParams={showDeleteModal?.deleteParams}
      />
    </div>
  );
};

export default MspSubscriptionAccountDetails;
